import React, { useMemo } from 'react'
import { CSVLink } from 'react-csv'

import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  DataTable,
  DataTableColumn,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  Typography
} from '@cuda-networks/bds-core'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Search, HelpOutline } from '@cuda-networks/bds-core/dist/Icons/Core'
import OutlinedDiv from 'components/libs/outlinedDiv/OutlinedDiv'

import { useFormatMessage } from 'lib/localization'
import { onKeyDown } from 'lib/keyEvents'

import { Cell } from 'components/libs/grid/cell'

import styles from 'components/pages/auditLog/auditLogStyles'
import { useAuditLogLogic } from 'components/pages/auditLog/useAuditLogLogic'
import { ModifiedAuditLog } from 'types/AuditLog'
import AuditLogFilter from 'components/pages/auditLog/auditLogFilter/AuditLogFilter'

const BASE_I18N_KEY = 'ess.audit_log'
const BASE_I18N_EXPORT_KEY = 'ess.audit_log.export'
const BASE_I18N_TABLE_KEY = 'ess.data_tables.audit_log'
const BASE_I18N_FILTER_KEY = 'ess.audit_log.action'

const AuditLog = () => {
  const classes = styles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatExportMessage = useFormatMessage(BASE_I18N_EXPORT_KEY)
  const formatTableMessage = useFormatMessage(BASE_I18N_TABLE_KEY)
  const formatFilterMessage = useFormatMessage(BASE_I18N_FILTER_KEY)
  const { inProgress, exportConfig, searchConfig, tableConfig, datePickerConfig } = useAuditLogLogic()

  return useMemo(
    () => (
      <>
        <CSVLink
          ref={exportConfig.exportRef}
          filename={exportConfig.fileName()}
          headers={exportConfig.headers.map(header => ({ ...header, label: formatExportMessage(header.label) }))}
          data={tableConfig.tableData.data}
          data-testid="export-link"
        />

        <Grid>
          <Grid item xs={12} className={classes.container}>
            <Grid className={classes.cardsRow}>
              <Grid className={classes.headerWrapper}>
                <Card elevation={0}>
                  <CardHeader
                    className={classes.cardHeader}
                    title={
                      <Grid className={classes.header}>
                        <Grid className={classes.headerTitle}>
                          <Typography variant="h2" data-testid="title">
                            {formatMessage('title')}
                          </Typography>
                          {/* TODO: Support page */}
                          <HelpOutline className={classes.helpIcon} />
                        </Grid>
                      </Grid>
                    }
                    data-testid="card-header"
                  />
                </Card>
              </Grid>
              <Grid className={classes.exportButton}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  className={classes.exportButton}
                  onClick={exportConfig.csvExport}
                  data-testid="export-button"
                >
                  {formatMessage('exportButton')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.container}>
            <Grid item xs={12} className={classes.filterRow}>
              <Grid item className={classes.searchText}>
                <TextField
                  className={classes.searchTextField}
                  placeholder={formatMessage('search_placeholder')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={searchConfig.search}
                  inputProps={{ maxLength: 128 }}
                  onChange={searchConfig.handleInputChange}
                  onKeyDown={onKeyDown(['Enter'], () => searchConfig.handleSearch())}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    startAdornment: (
                      <IconButton size="small" edge="start" data-id="search-button" data-testid="search-button">
                        <Search />
                      </IconButton>
                    )
                  }}
                  data-testid="search-input"
                />
              </Grid>
              <Grid className={classes.dateRange}>
                <div className={classes.datePickerWrapper}>
                  <OutlinedDiv label={formatMessage('start_date')} data-testid="start-datepicker">
                    <DatePicker
                      className={classes.datePicker}
                      name="start"
                      value={searchConfig.startDate}
                      {...datePickerConfig}
                    />
                  </OutlinedDiv>
                  <OutlinedDiv label={formatMessage('end_date')} data-testid="end-datepicker">
                    <DatePicker
                      className={classes.datePicker}
                      name="end"
                      value={searchConfig.endDate}
                      {...datePickerConfig}
                    />
                  </OutlinedDiv>
                </div>
              </Grid>
              <Grid className={classes.filter}>
                <AuditLogFilter
                  eventTypes={searchConfig.eventTypes}
                  onUpdateEventTypes={searchConfig.handleUpdateEventTypes}
                />
              </Grid>
            </Grid>
            <Grid item className={classes.filterChips}>
              {Object.entries(searchConfig.eventTypes).map(
                ([key, value]) =>
                  value && (
                    <div className={classes.filterChips} key={key}>
                      <Chip
                        key={key}
                        label={formatFilterMessage(key)}
                        className={classes.chip}
                        onDelete={(e: any) => searchConfig.handleRemoveEventType(key)}
                        size="small"
                        color="primary"
                      />
                    </div>
                  )
              )}
            </Grid>
            <Grid>
              <Box className={classes.logTableProgress}>{inProgress && <LinearProgress />}</Box>
              <DataTable
                pageConfig={{
                  pageable: { pageSizes: [10, 25, 50] },
                  skip: tableConfig.tableData.skip,
                  take: tableConfig.tableData.take,
                  total: tableConfig.tableData.total
                }}
                onPageChange={(e: any) => {
                  const { skip, take } = e.page
                  searchConfig.handlePageChange({ skip, take })
                }}
                onSortChange={searchConfig.handleSortChange}
                data={tableConfig.tableData}
                selectedField="selected"
                resizable
                sortable={{ allowUnsort: false, mode: 'single' }}
                sort={searchConfig.sort}
              >
                <DataTableColumn
                  field={tableConfig.columns.DATE}
                  {...tableConfig.columnsConfig[tableConfig.columns.DATE]}
                  title={formatTableMessage(`${tableConfig.columns.DATE}`)}
                  cell={({ dataItem }: { dataItem: ModifiedAuditLog }) => (
                    <Cell data-testid="date-cell">
                      <Typography variant="body2">{dataItem.formattedDate}</Typography>
                    </Cell>
                  )}
                />
                <DataTableColumn
                  field={tableConfig.columns.EVENT_TYPE}
                  {...tableConfig.columnsConfig[tableConfig.columns.EVENT_TYPE]}
                  title={formatTableMessage(`${tableConfig.columns.EVENT_TYPE}`)}
                  cell={({ dataItem }: { dataItem: ModifiedAuditLog }) => (
                    <Cell data-testid="event-type-cell">
                      <Typography variant="body2">{dataItem.eventType}</Typography>
                    </Cell>
                  )}
                />
                <DataTableColumn
                  field={tableConfig.columns.DESCRIPTION}
                  {...tableConfig.columnsConfig[tableConfig.columns.DESCRIPTION]}
                  title={formatTableMessage(`${tableConfig.columns.DESCRIPTION}`)}
                  cell={({ dataItem }: { dataItem: ModifiedAuditLog }) => (
                    <Cell data-testid="description-cell">
                      <Typography variant="body2">{dataItem.description}</Typography>
                    </Cell>
                  )}
                />
                <DataTableColumn
                  field={tableConfig.columns.USER}
                  {...tableConfig.columnsConfig[tableConfig.columns.USER]}
                  title={formatTableMessage(`${tableConfig.columns.USER}`)}
                  cell={({ dataItem }: { dataItem: ModifiedAuditLog }) => (
                    <Cell data-testid="user-cell">
                      <Typography variant="body2">{dataItem.userName}</Typography>
                    </Cell>
                  )}
                />
                <DataTableColumn
                  field={tableConfig.columns.IP_ADDRESS}
                  {...tableConfig.columnsConfig[tableConfig.columns.IP_ADDRESS]}
                  title={formatTableMessage(`${tableConfig.columns.IP_ADDRESS}`)}
                  cell={({ dataItem }: { dataItem: ModifiedAuditLog }) => (
                    <Cell data-testid="ip-address-cell">
                      <Typography variant="body2">{dataItem.ipAddress}</Typography>
                    </Cell>
                  )}
                />
              </DataTable>
            </Grid>
          </Grid>
        </Grid>
      </>
    ),
    [
      classes,
      formatMessage,
      formatExportMessage,
      formatTableMessage,
      formatFilterMessage,
      exportConfig,
      searchConfig,
      tableConfig,
      datePickerConfig,
      inProgress
    ]
  )
}

export default AuditLog
